import React, { useEffect, useState } from "react";
import { COOKIE_BANNER_LOAD_EVENT_NAME } from "web/react/constants";
import useOneTapScript from "web/react/hooks/use-google-one-tap/use-google-one-tap";
import { useUrlData } from "web/react/hooks/use-url-data/use-url-data";
import { AuthType } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import cookie from "web/script/utils/cookie";

declare global {
    interface Window {
        google: any;
        [key: string]: any;
    }
}

interface IGoogleCallbackResponse {
    clientId?: string;
    credential?: string;
    select_by?: string;
}

const googleClientScriptURL = "https://accounts.google.com/gsi/client";

function googleOneTapAnalytics(notification: any): void {
    if (notification.getMomentType() !== "skipped") {
        analytics.event("google_one_tap", "shown");
    }
}

function processGoogleCredential(
    data: IGoogleCallbackResponse,
    currentUrl: URL | undefined,
    authType: AuthType
): void {
    if (data?.credential && currentUrl) {
        const fullUrl = currentUrl.pathname + currentUrl.search;
        try {
            // Create a hidden form with a credential input instead of using our 'requester'
            // helper as we don't want to follow 301s
            const form = document.createElement("form");
            const action = `${
                window.location.origin
            }/social/complete/google-one-tap/?type=${authType}&next=${encodeURIComponent(fullUrl)}`;
            form.method = "POST";
            form.action = action;

            // Create a hidden input for the credential
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "id_token";
            input.value = data.credential;
            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();

            // Remove the form from the DOM (not sure if needed, as the page will reload on login)
            document.body.removeChild(form);
        } catch (e) {
            console.error("Failed to log in with Google One Tap:", e);
        }
    }
}

interface GoogleOneTapProps {
    disabled?: boolean;
    onSuccess?: (response: any) => void;
    googleAccountConfigs: any;
    disableCancelOnUnmount?: boolean;
    client_id: string;
}

export default function GoogleOneTap({
    googleAccountConfigs,
    disableCancelOnUnmount = false,
}: GoogleOneTapProps): JSX.Element {
    const urlData = useUrlData();
    const script = useOneTapScript(googleClientScriptURL);
    const pageType = environment.get("pageType");
    const deviceFamily = environment.get("deviceFamily");

    const [optanonCookie, setOptanonCookie] = useState<string | null | undefined>(
        cookie("OptanonAlertBoxClosed")
    );

    useEffect(() => {
        // Function to check for consent interaction and update cookie as using the state of
        // the cookie wasn't as checking just cookie wasn't sufficient to pickup the change
        const updateCookieState = (): void => {
            const currentValue = cookie("OptanonAlertBoxClosed");
            if (currentValue !== optanonCookie) {
                setOptanonCookie(currentValue);
            }
        };

        // Listen for OneTrust's consent changed event
        const handleConsentChanged = (): void => {
            // Small delay to ensure cookie is set - fixes
            // a bug where it was not set in time.
            setTimeout(updateCookieState, 100);
        };

        // window.OneTrust.OnConsentChanged will not be populated until the event listener
        // associated with COOKIE_BANNER_LOAD_EVENT_NAME fires. To prevent errors, only
        // run this line if that is the case.
        if (window.OneTrust && window.OneTrust.OnConsentChanged) {
            window.OneTrust.OnConsentChanged(handleConsentChanged);
        }

        const handleOneTrustLoaded = (): void => {
            if (window.OneTrust) {
                window.OneTrust.OnConsentChanged(handleConsentChanged);
            }
            setTimeout(updateCookieState, 100);
        };

        window.addEventListener(COOKIE_BANNER_LOAD_EVENT_NAME, handleOneTrustLoaded);

        // Check once on mount
        updateCookieState();

        // Clean up
        return () => {
            window.removeEventListener(COOKIE_BANNER_LOAD_EVENT_NAME, handleOneTrustLoaded);
        };
    }, [optanonCookie]);

    useEffect(() => {
        // Skip if user is logged in or if cookie condition isn't met (as on FedCM browsers),
        // one tap obscures the cookie banner, which we don't want
        if (userProfiler.isLoggedIn() || (!optanonCookie && deviceFamily !== "desktop")) {
            return;
        }

        // This is used for analytics
        let authType: AuthType = "normal";
        if (pageType === "homepage") {
            authType = "google_one_tap_homepage";
        } else if (pageType === "feed") {
            authType = "google_one_tap_feeds";
        } else if (pageType === "search") {
            authType = "google_one_tap_search";
        }

        if (window.google && script === "ready") {
            // Add safety check to ensure clientId exists before initializing. Doesn't
            // fix a bug, but means less GSI_LOGGER errors in console from Google
            if (googleAccountConfigs?.clientId) {
                window.google.accounts.id.initialize({
                    client_id: googleAccountConfigs.clientId,
                    callback: (response: IGoogleCallbackResponse) =>
                        processGoogleCredential(response, urlData?.newURL, authType),
                    cancel_on_tap_outside: false,
                    context: "signin",
                    itp_support: true,
                    sign_up_language: environment.get("language"),
                    moment_callback: googleOneTapAnalytics,
                    prompt_parent_id: "g_id_onload",
                });
            } else {
                console.warn("Google One Tap initialization skipped: Missing client_id");
            }

            window.google.accounts.id.prompt();

            return () => {
                if (!disableCancelOnUnmount) {
                    window.google.accounts.id.cancel();
                }
            };
        }
    }, [script, googleAccountConfigs, disableCancelOnUnmount, urlData?.newURL, optanonCookie]);

    // Early return with empty div if user is logged in or cookie condition isn't met
    if (userProfiler.isLoggedIn() || (!optanonCookie && deviceFamily !== "desktop")) {
        return <div id="g_id_onload" style={{ display: "none" }} />;
    }

    return (
        <div
            id="g_id_onload"
            data-prompt_parent_id="g_id_onload"
            data-cancel_on_tap_outside="false"
            style={{
                position: "fixed",
                top: "32px",
                right: "32px",
                width: "auto",
                height: "auto",
                zIndex: "1001",
            }}
        />
    );
}
